import React from "react";
import { LifeBuoy } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import SupportContactActions from "@/b2b/components/SupportContactActions";

/**
 * A fixed light theme for the WhatsApp/Call buttons inside this dialog — NOT the partner
 * `theme` those buttons normally receive on the full-bleed dead-end screens.
 *
 * `JourneyPillAction`/`SupportContactActions` read `theme?.textColor` and apply it as a
 * Tailwind class. Passing no theme at all does NOT fall back to a sane default: `theme?.textColor`
 * evaluates to `undefined`, which the template literal turns into the literal string
 * "undefined" — not a real class, so nothing is applied — and the button text then inherits the
 * app's GLOBAL dark-mode `--foreground` (near-white), which is invisible on this dialog's white
 * surface. Confirmed live: the icons/labels were unreadable in production. Always pass an
 * explicit theme here.
 */
const DIALOG_THEME = { textColor: "text-gray-700" };

/**
 * The "Support" tap on a stuck onboarding-step card (KYC / Income / E-Sign, any WAITING state)
 * opens this instead of the old dead `alert()`. Reuses `SupportContactActions` — the same
 * WhatsApp/Call pair the dedicated dead-end screens use — so tapping either button also raises a
 * deduped/cooled-down ops alert via its `topic` prop, rather than duplicating that wiring here.
 */
export default function SupportRequestDialog({
  open,
  onOpenChange,
  topic,
  problemLine,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm mx-auto bg-white border border-gray-200">
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <LifeBuoy className="h-4 w-4" strokeWidth={1.75} />
            Need help?
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-gray-600">
          Reach us on WhatsApp or by phone and we'll help you sort this out.
        </DialogDescription>
        <SupportContactActions
          theme={DIALOG_THEME}
          topic={topic}
          problemLine={problemLine}
        />
      </DialogContent>
    </Dialog>
  );
}
